import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { DocumentHeader } from "../../components/DocumentHeader";
import { SignaturePad } from "../../components/SignaturePad";
import { DocumentClosureBar } from "../../components/DocumentClosureBar";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, formatApiError, UPLOADS_BASE } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Save, Mail, PackagePlus, Search, Package, ThumbsUp, ThumbsDown, FileCheck2, AlertTriangle, PenLine, ArrowLeft, Wallet } from "lucide-react";

const DEFAULT_MENTIONS = "Ce devis ne constitue pas une facture. Tout travail supplémentaire non prévu fera l'objet d'un devis complémentaire.";
const MENTION_ACCEPTATION = "Devis reçu avant l'exécution des travaux — Bon pour accord";

const EMPTY_DATA = {
  numero: "", client_id: null,
  client_nom: "", client_tel: "", client_email: "", client_adresse: "",
  items: [], mentions_legales: DEFAULT_MENTIONS,
  validite_jours: 30, devis_payant: false, montant_etude: "",
  acompte_type: null, acompte_valeur: "",
  delai_intervention: "", conditions_garantie: "",
  acceptation_mention: false,
};

export default function DevisFormPage() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sigRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailMessage, setMailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  const [articleSearch, setArticleSearch] = useState("");
  const [data, setData] = useState({
    ...EMPTY_DATA,
    client_id: searchParams.get("client_id") || null,
    client_nom: searchParams.get("client_nom") || "",
    client_tel: searchParams.get("client_tel") || "",
    client_email: searchParams.get("client_email") || "",
  });

  useEffect(() => {
    (async () => {
      const [shopsRes] = await Promise.all([api.get("/shops")]);
      const myShop = shopsRes.data.find((s) => s.id === user.effective_shop_id);
      if (!myShop && isNew) {
        toast.error("Aucune boutique n'est assignée à votre compte. Contactez un administrateur.");
      }
      setShop(myShop || shopsRes.data[0]);
      if (hasPerm(user, "stock", "view")) {
        api.get("/stock").then((r) => setArticles(r.data)).catch(() => {});
      }
      if (!isNew) {
        const { data: item } = await api.get(`/devis/${id}`);
        setData(item);
      } else {
        // Nouveau devis : « mentions légales par défaut » de la boutique
        // (menu ⋯ de la liste des devis). Simple pré-remplissage, modifiable
        // sur ce devis sans toucher au texte par défaut.
        try {
          const { data: def } = await api.get("/devis/settings/mentions-par-defaut");
          if (def?.mentions_legales) setData((d) => ({ ...d, mentions_legales: def.mentions_legales }));
        } catch (e) { /* on garde le texte intégré */ }
      }
    })();
  }, [id]);

  const tvaApplicable = !isNew ? data.tva_applicable_snapshot !== false : shop?.tva_applicable !== false;

  const today = new Date().toLocaleDateString("fr-FR");
  const totalHt = data.items.reduce((s, i) => s + i.prix_unitaire * i.quantite, 0);
  const tvaBreakdown = (() => {
    if (!tvaApplicable) return [];
    const groups = {};
    for (const i of data.items) {
      const rate = Number(i.tva_percent ?? 20);
      const lineHt = i.prix_unitaire * i.quantite;
      const g = groups[rate] || { tva_percent: rate, base_ht: 0, montant_tva: 0 };
      g.base_ht += lineHt;
      g.montant_tva += (lineHt * rate) / 100;
      groups[rate] = g;
    }
    return Object.values(groups).sort((a, b) => b.tva_percent - a.tva_percent);
  })();
  const totalTva = tvaBreakdown.reduce((s, g) => s + g.montant_tva, 0);
  const totalTtc = tvaApplicable ? totalHt + totalTva : totalHt;
  const montantAcompte = (() => {
    if (!data.acompte_type || !data.acompte_valeur) return 0;
    if (data.acompte_type === "pourcentage") return (totalTtc * Number(data.acompte_valeur)) / 100;
    return Math.min(Number(data.acompte_valeur), totalTtc);
  })();
  const soldeApresAcompte = totalTtc - montantAcompte;

  const addItem = () => setData({ ...data, items: [...data.items, { nom: "", prix_unitaire: 0, quantite: 1, tva_percent: 20 }] });
  const updateItem = (idx, field, val) => {
    const items = [...data.items];
    items[idx] = { ...items[idx], [field]: val };
    setData({ ...data, items });
  };
  const removeItem = (idx) => setData({ ...data, items: data.items.filter((_, i) => i !== idx) });

  const addArticleFromStock = (article) => {
    setData((d) => ({
      ...d,
      items: [...d.items, { nom: article.nom, prix_unitaire: Number(article.prix ?? article.prix_ttc ?? 0), quantite: 1, tva_percent: Number(article.tva_percent ?? 20) }],
    }));
    toast.success(`« ${article.nom} » ajouté au devis`);
  };

  const articleSearchQ = articleSearch.trim().toLowerCase();
  const filteredArticles = articles.filter((a) => {
    if (!articleSearchQ) return true;
    return (a.nom || "").toLowerCase().includes(articleSearchQ)
      || (a.categorie || "").toLowerCase().includes(articleSearchQ)
      || (a.code || "").toLowerCase().includes(articleSearchQ);
  });
  const articlesEnStock = filteredArticles.filter((a) => Number(a.quantite ?? 0) > 0);
  const articlesHorsStock = filteredArticles.filter((a) => Number(a.quantite ?? 0) <= 0);

  const hasEditPerm = data.is_shared_to_me
    ? data.share_mode === "write"
    : (isNew ? hasPerm(user, "devis", "create") : hasPerm(user, "devis", "edit"));
  const isCloturee = data.status === "cloture";
  const canEdit = hasEditPerm && !isCloturee;

  // `persist` est la fonction commune au bouton "Enregistrer" classique et au
  // flux "Signature" (voir plus bas) : elle construit le payload à partir de
  // l'état courant du formulaire, avec la possibilité de forcer certains
  // champs (`overrides`, utilisé pour injecter la mention manuscrite et la
  // signature une fois validées dans la fenêtre de signature — même principe
  // que RepriseFormPage.buildPayload/persist).
  const buildPayload = (overrides = {}) => {
    const signature_data = overrides.signature_data ?? (sigRef.current?.toDataURL() || data.signature_data || "");
    const mention_bon_pour_accord = overrides.mention_bon_pour_accord ?? data.mention_bon_pour_accord ?? "";
    return {
      ...data,
      signature_data,
      mention_bon_pour_accord,
      acceptation_mention: overrides.acceptation_mention ?? data.acceptation_mention,
      montant_etude: data.devis_payant ? Number(data.montant_etude) || 0 : null,
      acompte_valeur: data.acompte_type ? Number(data.acompte_valeur) || 0 : null,
    };
  };

  // Renvoie l'identifiant du document créé/mis à jour, ou null si la
  // sauvegarde n'a pas pu avoir lieu — utilisé par le flux de signature pour
  // savoir s'il peut enchaîner sur l'étape suivante.
  const persist = async (overrides = {}, { refresh = false } = {}) => {
    if (isNew && !shop) {
      toast.error("Impossible de créer : aucune boutique assignée à votre compte.");
      return null;
    }
    const payload = buildPayload(overrides);
    try {
      if (isNew) {
        const { data: created } = await api.post("/devis", { ...payload, shop_id: shop.id });
        toast.success(`Devis ${created.numero} créé`);
        navigate(`/devis/${created.id}`);
        return created.id;
      }
      const { data: updated } = await api.patch(`/devis/${id}`, payload);
      toast.success("Devis mis à jour");
      if (refresh) setData(updated);
      return id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  const save = () => persist({}, { refresh: true });

  const setDecision = async (decision) => {
    setDecisionBusy(true);
    try {
      const { data: updated } = await api.post(`/devis/${id}/decision`, { decision });
      setData(updated);
      toast.success(decision === "accepte" ? "Devis marqué accepté et clôturé" : "Devis marqué refusé et clôturé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDecisionBusy(false);
    }
  };

  // --- Flux "Signature" ---
  // Bouton unique (comme pour les reprises) qui ouvre une fenêtre en 3 étapes :
  //  1. "intro"     : mentions légales + aperçu PDF du devis, puis le client
  //                   choisit « J'accepte » ou « Je refuse ».
  //  2. "mention"    : (si accepté) le client écrit lui-même « BON POUR
  //                   ACCORD » dans un cadre de dessin.
  //  3. "signature"  : (si accepté) le client signe.
  // Un refus clôture IMMÉDIATEMENT le document (voir devis_decision côté
  // serveur, qui clôture sur toute décision si ce n'était pas déjà fait) et
  // n'a pas besoin de mention ni de signature. Une acceptation ne clôture et
  // n'enregistre la décision qu'une fois la signature de l'étape 3 validée
  // (voir _cloture_fields_with_decision côté serveur).
  const mentionSigRef = useRef(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signStep, setSignStep] = useState("intro"); // "intro" | "mention" | "signature"
  const [pendingMention, setPendingMention] = useState("");
  const [signBusy, setSignBusy] = useState(false);

  const openSignFlow = async () => {
    setSignBusy(true);
    const savedId = await persist({}, { refresh: true });
    setSignBusy(false);
    if (!savedId) return;
    setPendingMention("");
    setSignStep("intro");
    setSignOpen(true);
  };

  const refuseDevis = async () => {
    setSignBusy(true);
    try {
      const { data: updated } = await api.post(`/devis/${id}/decision`, { decision: "refuse" });
      setData(updated);
      toast.success("Devis refusé par le client — document clôturé");
      setSignOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSignBusy(false);
    }
  };

  const acceptDevis = () => setSignStep("mention");

  const validateMentionStep = () => {
    const value = mentionSigRef.current?.toDataURL() || "";
    if (!value) {
      toast.error("Faites écrire « BON POUR ACCORD » dans le cadre avant de valider.");
      return;
    }
    setPendingMention(value);
    setSignStep("signature");
  };

  const finalizeSignature = async () => {
    const value = sigRef.current?.toDataURL() || "";
    if (!value) {
      toast.error("La signature est requise avant de clôturer.");
      return;
    }
    setSignBusy(true);
    const savedId = await persist(
      { mention_bon_pour_accord: pendingMention, signature_data: value, acceptation_mention: true },
      { refresh: true },
    );
    setSignBusy(false);
    if (savedId) {
      setSignOpen(false);
      toast.success("Devis accepté, signé et clôturé");
    }
  };

  const closeSignDialog = (open) => {
    if (!open) setSignStep("intro");
    setSignOpen(open);
  };

  // Transformer un devis accepté/signé en facture : ouvre l'onglet Vente avec
  // le panier déjà rempli (client, lignes, TVA) et le type de document sur
  // "Facture" — l'encaissement réel (paiements, verrouillage fiscal,
  // numérotation) reste entièrement géré par la Caisse, jamais recréé ici.
  // Le devis n'est marqué "transformé" que lorsque cette facture est
  // effectivement validée (voir caisse_routes.create_ticket côté serveur).
  const transformerEnFacture = () => {
    navigate("/caisse", {
      state: {
        fromDevis: {
          id: data.id,
          numero: data.numero,
          client_id: data.client_id,
          client_nom: data.client_nom,
          client_tel: data.client_tel,
          client_email: data.client_email,
          client_adresse: data.client_adresse,
          items: data.items,
          montant_acompte: data.montant_acompte,
        },
      },
    });
  };

  // Acompte : ouvre la caisse avec un ticket « Acompte devis n°… » à confirmer.
  // Le taux de TVA retenu est celui qui pèse le plus dans le devis.
  const encaisserAcompte = async () => {
    try {
      const { data: ac } = await api.get(`/devis/${data.id}/acomptes`);
      const parTaux = {};
      for (const it of data.items || []) {
        const t = it.tva_percent ?? 20;
        parTaux[t] = (parTaux[t] || 0) + (Number(it.prix_unitaire) || 0) * (Number(it.quantite) || 1);
      }
      const tva = Number(Object.entries(parTaux).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 20);
      const demande = Math.max(0, (data.montant_acompte || 0) - ac.total_acomptes);
      navigate("/caisse", {
        state: {
          fromDevisAcompte: {
            id: data.id, numero: data.numero, client_id: data.client_id, client_nom: data.client_nom,
            client_adresse: data.client_adresse, client_email: data.client_email,
            reste: ac.reste_a_percevoir, suggere: demande > 0 ? demande : ac.reste_a_percevoir, tva_percent: tva,
          },
        },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openMailDialog = () => {
    setMailTo(data.client_email || "");
    setMailSubject(`Votre devis ${data.numero || ""} - ${shop?.nom || ""}`);
    setMailMessage(`Bonjour ${data.client_nom || ""},\n\nVeuillez trouver ci-joint votre devis ${data.numero || ""}.\n\nCordialement,\n${shop?.nom || ""}`);
    setMailOpen(true);
  };

  const sendMail = async () => {
    if (!mailTo) { toast.error("Saisissez l'adresse e-mail du client"); return; }
    setSending(true);
    try {
      const res = await api.post(`/devis/${id}/send-email`, { to_email: mailTo, subject: mailSubject, message: mailMessage });
      toast.success(`Devis envoyé à ${res.data.sent_to}`);
      setMailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const fmt = (n) => Number(n || 0).toFixed(2);

  return (
    <Layout title="Devis">
      <div className="a4-sheet max-w-4xl mx-auto p-4 sm:p-8 md:p-12 rounded-md">
        <DocumentHeader shop={shop} numero={data.numero || "(auto)"} />
        {data.is_shared_to_me && (
          <p className="text-xs text-center text-muted-foreground mb-4" data-testid="devis-shared-banner">
            Document partagé par {data.shared_by_label} — {data.share_mode === "write" ? "lecture / écriture" : "lecture seule"}
          </p>
        )}
        <h2 className="font-heading text-xl font-bold text-center mb-6">DEVIS</h2>

        <DocumentClosureBar
          module="devis"
          id={isNew ? null : id}
          data={data}
          canEdit={hasEditPerm}
          canDelete={hasPerm(user, "devis", "delete")}
          onUpdated={setData}
        />

        {!isNew && data.expire && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex items-center gap-2 mb-4 no-print" data-testid="devis-expire-banner">
            <AlertTriangle size={16} className="text-amber-600 shrink-0" />
            <p className="text-amber-700 dark:text-amber-400">Ce devis a dépassé sa date de validité ({data.date_validite}) sans réponse du client.</p>
          </div>
        )}

        {!isNew && data.decision && (
          <div
            className={`rounded-lg border p-3 text-sm flex items-center gap-2 mb-4 no-print ${data.decision === "accepte" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "border-destructive/40 bg-destructive/10 text-destructive"}`}
            data-testid="devis-decision-banner"
          >
            {data.decision === "accepte" ? <ThumbsUp size={16} className="shrink-0" /> : <ThumbsDown size={16} className="shrink-0" />}
            <p>Devis {data.decision === "accepte" ? "accepté" : "refusé"} par le client{data.decision_date ? ` le ${new Date(data.decision_date).toLocaleDateString("fr-FR")}` : ""}.</p>
          </div>
        )}

        {!isNew && isCloturee && !data.annule && !data.decision && hasEditPerm && (
          <div className="flex gap-2 mb-4 no-print">
            <Button size="sm" variant="outline" className="gap-2" disabled={decisionBusy} onClick={() => setDecision("accepte")} data-testid="devis-decision-accepte">
              <ThumbsUp size={14} />Marquer accepté
            </Button>
            <Button size="sm" variant="outline" className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10" disabled={decisionBusy} onClick={() => setDecision("refuse")} data-testid="devis-decision-refuse">
              <ThumbsDown size={14} />Marquer refusé
            </Button>
          </div>
        )}

        {!isNew && isCloturee && !data.annule && data.transforme_facture_numero && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm flex items-center gap-2 mb-4 no-print" data-testid="devis-transforme-banner">
            <FileCheck2 size={16} className="text-emerald-600 shrink-0" />
            <p className="text-emerald-700 dark:text-emerald-400">Transformé en facture {data.transforme_facture_numero}.</p>
          </div>
        )}
        {!isNew && (data.acomptes || []).length > 0 && (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm mb-4 no-print" data-testid="devis-acomptes">
            <p className="font-semibold mb-1">Acomptes encaissés</p>
            {data.acomptes.map((a) => (
              <p key={a.ticket_id} className="text-muted-foreground" data-testid={`devis-acompte-${a.ticket_numero}`}>
                {a.date} — ticket {a.ticket_numero} : <b>{Number(a.montant_ttc).toFixed(2)} €</b>
                {a.deduit_facture_numero ? ` (déduit de la facture ${a.deduit_facture_numero})` : " (sera déduit de la facture)"}
              </p>
            ))}
          </div>
        )}
        {!isNew && isCloturee && !data.annule && data.decision !== "refuse" && !data.transforme_facture_numero && hasPerm(user, "caisse", "access") && (
          <div className="flex justify-end gap-2 mb-4 no-print">
            <Button size="sm" variant="outline" className="gap-2" onClick={encaisserAcompte} data-testid="devis-encaisser-acompte">
              <Wallet size={14} />Encaisser un acompte
            </Button>
            <Button size="sm" className="gap-2" onClick={transformerEnFacture} data-testid="devis-transformer-facture">
              <FileCheck2 size={14} />Transformer en facture
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm">
          <div><span className="font-semibold">Vendeur:</span> {data.vendeur_nom || `${user.prenom} ${user.nom}`}</div>
          <div><span className="font-semibold">Date:</span> {data.date ? `${data.date}${data.heure ? " " + data.heure : ""}` : today}</div>
          <div className="flex items-center gap-2">
            <span className="font-semibold shrink-0">Validité:</span>
            {canEdit ? (
              <span className="flex items-center gap-1.5">
                <Input
                  type="number" min={1} max={365} className="w-20 h-8"
                  disabled={!canEdit} data-testid="devis-validite-jours"
                  value={data.validite_jours} onChange={(e) => setData({ ...data, validite_jours: Number(e.target.value) })}
                />
                <span className="text-muted-foreground">jours{data.date_validite ? ` (jusqu'au ${data.date_validite})` : ""}</span>
              </span>
            ) : (
              <span>{data.date_validite ? `jusqu'au ${data.date_validite}` : `${data.validite_jours} jours`}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold shrink-0">Établissement du devis:</span>
            {canEdit ? (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox data-testid="devis-payant-checkbox" checked={data.devis_payant} onCheckedChange={(v) => setData({ ...data, devis_payant: !!v })} />
                <span>Payant</span>
                {data.devis_payant && (
                  <Input
                    type="number" step="0.01" min={0} className="w-24 h-8 ml-1"
                    placeholder="Montant"
                    data-testid="devis-montant-etude"
                    value={data.montant_etude ?? ""}
                    onChange={(e) => setData({ ...data, montant_etude: e.target.value })}
                  />
                )}
                {data.devis_payant && <span>€</span>}
              </label>
            ) : (
              <span>{data.devis_payant ? `Payant — ${fmt(data.montant_etude)} €` : "Gratuit"}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
          <Input data-testid="devis-client-nom" disabled={!canEdit} placeholder="Nom client" value={data.client_nom} onChange={(e) => setData({ ...data, client_nom: e.target.value })} />
          <Input data-testid="devis-client-tel" disabled={!canEdit} placeholder="Téléphone" value={data.client_tel} onChange={(e) => setData({ ...data, client_tel: e.target.value })} />
          <Input data-testid="devis-client-email" disabled={!canEdit} placeholder="E-mail" value={data.client_email} onChange={(e) => setData({ ...data, client_email: e.target.value })} />
          <Input data-testid="devis-client-adresse" disabled={!canEdit} placeholder="Adresse" value={data.client_adresse} onChange={(e) => setData({ ...data, client_adresse: e.target.value })} />
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
            <p className="font-semibold text-sm">Lignes du devis</p>
            {canEdit && (
              <div className="flex gap-2">
                {hasPerm(user, "stock", "view") && (
                  <Button size="sm" variant="outline" data-testid="devis-add-article" onClick={() => { setArticleSearch(""); setArticlePickerOpen(true); }} className="gap-1">
                    <PackagePlus size={14} />Ajouter article
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid="devis-add-item" onClick={addItem} className="gap-1"><Plus size={14} />Ligne</Button>
              </div>
            )}
          </div>
          {data.items.map((item, idx) => (
            <div key={idx} className={`grid grid-cols-2 ${tvaApplicable ? "sm:grid-cols-12" : "sm:grid-cols-10"} gap-2 mb-2 items-center`}>
              <Input disabled={!canEdit} data-testid={`devis-item-nom-${idx}`} className="col-span-2 sm:col-span-5" placeholder="Désignation" value={item.nom} onChange={(e) => updateItem(idx, "nom", e.target.value)} />
              <Input disabled={!canEdit} data-testid={`devis-item-qte-${idx}`} type="number" className="col-span-1 sm:col-span-2" value={item.quantite} onChange={(e) => updateItem(idx, "quantite", Number(e.target.value))} />
              <Input disabled={!canEdit} data-testid={`devis-item-prix-${idx}`} type="number" className="col-span-1 sm:col-span-2" placeholder="Prix unit. HT" value={item.prix_unitaire} onChange={(e) => updateItem(idx, "prix_unitaire", Number(e.target.value))} />
              {tvaApplicable && (
                <Input disabled={!canEdit} data-testid={`devis-item-tva-${idx}`} type="number" className="col-span-1 sm:col-span-2" placeholder="TVA %" value={item.tva_percent ?? 20} onChange={(e) => updateItem(idx, "tva_percent", Number(e.target.value))} />
              )}
              {canEdit && <button data-testid={`devis-item-remove-${idx}`} onClick={() => removeItem(idx)} className="text-destructive justify-self-end sm:justify-self-auto"><Trash2 size={16} /></button>}
            </div>
          ))}
          <div className="text-right mt-3 space-y-0.5 text-sm" data-testid="devis-totals">
            <p>Total HT: <span className="font-medium">{fmt(totalHt)} €</span></p>
            {tvaApplicable ? (
              <>
                {tvaBreakdown.map((g) => (
                  <p key={g.tva_percent} className="text-muted-foreground text-xs">
                    dont TVA {g.tva_percent}% (sur {fmt(g.base_ht)} €): {fmt(g.montant_tva)} €
                  </p>
                ))}
                <p className="font-bold">Total TTC: {fmt(totalTtc)} €</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">TVA non applicable, art. 293 B du CGI</p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <label className="text-sm font-semibold block mb-2">Acompte demandé à la commande</label>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              disabled={!canEdit}
              value={data.acompte_type || "aucun"}
              onValueChange={(v) => setData({ ...data, acompte_type: v === "aucun" ? null : v, acompte_valeur: v === "aucun" ? "" : data.acompte_valeur })}
            >
              <SelectTrigger className="w-44" data-testid="devis-acompte-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aucun">Aucun acompte</SelectItem>
                <SelectItem value="pourcentage">Pourcentage (%)</SelectItem>
                <SelectItem value="montant">Montant fixe (€)</SelectItem>
              </SelectContent>
            </Select>
            {data.acompte_type && (
              <Input
                type="number" step="0.01" min={0} className="w-32"
                disabled={!canEdit} data-testid="devis-acompte-valeur"
                placeholder={data.acompte_type === "pourcentage" ? "%" : "€"}
                value={data.acompte_valeur ?? ""}
                onChange={(e) => setData({ ...data, acompte_valeur: e.target.value })}
              />
            )}
            {data.acompte_type && Number(data.acompte_valeur) > 0 && (
              <span className="text-sm text-muted-foreground" data-testid="devis-acompte-calcule">
                soit {fmt(montantAcompte)} € — solde {fmt(soldeApresAcompte)} €
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-sm font-semibold">Délai d'intervention estimé</label>
            <Input data-testid="devis-delai-intervention" disabled={!canEdit} placeholder="Ex: 48h ouvrées" value={data.delai_intervention} onChange={(e) => setData({ ...data, delai_intervention: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold">Conditions de garantie</label>
            <Input data-testid="devis-conditions-garantie" disabled={!canEdit} placeholder="Ex: 6 mois pièces et main d'œuvre" value={data.conditions_garantie} onChange={(e) => setData({ ...data, conditions_garantie: e.target.value })} />
          </div>
        </div>

        <label className="text-sm font-semibold">Mentions légales</label>
        <Textarea data-testid="devis-mentions" disabled={!canEdit} className="mb-6 h-20" value={data.mentions_legales} onChange={(e) => setData({ ...data, mentions_legales: e.target.value })} />

        {/* La mention manuscrite "BON POUR ACCORD" et la signature ne sont
            plus saisies directement sur la page (même principe que la fiche
            de reprise) : elles se font via le bouton "Signature" ci-dessous,
            qui ouvre une fenêtre dédiée en 3 étapes (mentions légales +
            aperçu PDF puis accepter/refuser, puis mention manuscrite, puis
            signature). Une fois le devis clôturé, on affiche ici un aperçu en
            lecture seule de ce qui a été enregistré. */}
        {isCloturee && (data.mention_bon_pour_accord || data.signature_data) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2" data-testid="devis-signatures-preview">
            {data.mention_bon_pour_accord && (
              <div>
                <label className="text-sm font-semibold block mb-1">Bon pour accord</label>
                <div className="w-full h-36 bg-slate-50 dark:bg-zinc-800 border border-border rounded-lg flex items-center justify-center overflow-hidden">
                  <img src={data.mention_bon_pour_accord} alt="Bon pour accord" className="max-w-full max-h-full object-contain" />
                </div>
              </div>
            )}
            {data.signature_data && (
              <div>
                <label className="text-sm font-semibold block mb-1">Signature</label>
                <div className="w-full h-36 bg-slate-50 dark:bg-zinc-800 border border-border rounded-lg flex items-center justify-center overflow-hidden">
                  <img src={data.signature_data} alt="Signature" className="max-w-full max-h-full object-contain" />
                </div>
              </div>
            )}
          </div>
        )}
        {isCloturee && (data.mention_bon_pour_accord || data.signature_data) && (
          <p className="text-xs text-muted-foreground mt-2 mb-6">
            Document clôturé : la mention et la signature déjà apposées ne sont plus modifiables.
          </p>
        )}
        {canEdit && !isNew && (
          <div className="mb-6">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={openSignFlow}
              disabled={signBusy}
              data-testid="devis-signature-button"
            >
              <PenLine size={16} />Signature
            </Button>
          </div>
        )}

        <div className="flex gap-3 mt-6 no-print flex-wrap">
          {canEdit && <Button data-testid="devis-save-button" onClick={save} className="gap-2"><Save size={16} />Enregistrer</Button>}
          {!isNew && (
            <a href={`${api.defaults.baseURL}/devis/${id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="devis-pdf-button" className="gap-2"><Printer size={16} />PDF</Button>
            </a>
          )}
          {!isNew && (
            <Button variant="outline" className="gap-2" onClick={openMailDialog} data-testid="devis-send-email-button">
              <Mail size={16} />Envoyer par e-mail
            </Button>
          )}
        </div>
      </div>

      <Dialog open={signOpen} onOpenChange={closeSignDialog}>
        <DialogContent
          data-testid="devis-sign-dialog"
          className={signStep === "intro" ? "sm:max-w-3xl" : "sm:max-w-2xl"}
        >
          <DialogHeader>
            <DialogTitle>
              {signStep === "intro" ? "Accord du client" : signStep === "mention" ? "Bon pour accord" : "Signature"}
            </DialogTitle>
          </DialogHeader>

          {signStep === "intro" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm space-y-2 max-h-40 overflow-y-auto" data-testid="devis-sign-mentions">
                {data.mentions_legales && <p className="whitespace-pre-wrap">{data.mentions_legales}</p>}
                <p className="font-semibold">{MENTION_ACCEPTATION}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Aperçu du devis</p>
                <iframe
                  title="Aperçu du devis"
                  src={`${api.defaults.baseURL}/devis/${id}/pdf`}
                  className="w-full h-72 sm:h-96 rounded-lg border border-border"
                  data-testid="devis-sign-pdf-preview"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Faites lire les mentions légales et l'aperçu ci-dessus au client, puis demandez-lui sa décision.
              </p>
            </div>
          )}

          {signStep === "mention" && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Le client écrit lui-même « BON POUR ACCORD » dans le cadre ci-dessous.
              </p>
              {/* `key` explicite : sans elle, React réutilise la même instance
                  de <SignaturePad> en passant à l'étape signature, et le
                  <canvas> n'est jamais réellement recréé (voir même remarque
                  dans RepriseFormPage). */}
              <SignaturePad key="mention-pad" ref={mentionSigRef} testId="devis-mention-pad" heightClass="h-64 sm:h-72" />
            </div>
          )}

          {signStep === "signature" && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Faites signer le client dans le cadre ci-dessous.
              </p>
              <SignaturePad key="signature-pad" ref={sigRef} testId="devis-signature-pad" heightClass="h-64 sm:h-72" />
            </div>
          )}

          <DialogFooter className="gap-2">
            {signStep === "intro" && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10"
                  onClick={refuseDevis}
                  disabled={signBusy}
                  data-testid="devis-sign-refuse-button"
                >
                  <ThumbsDown size={15} />Je refuse le devis
                </Button>
                <Button type="button" className="gap-2" onClick={acceptDevis} disabled={signBusy} data-testid="devis-sign-accept-button">
                  <ThumbsUp size={15} />J'accepte le devis
                </Button>
              </>
            )}
            {signStep === "mention" && (
              <Button data-testid="devis-sign-validate-mention-button" onClick={validateMentionStep} disabled={signBusy} className="gap-2">
                Valider
              </Button>
            )}
            {signStep === "signature" && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-2"
                  data-testid="devis-sign-back-button"
                  onClick={() => setSignStep("mention")}
                  disabled={signBusy}
                >
                  <ArrowLeft size={15} />Retour
                </Button>
                <Button data-testid="devis-sign-finalize-button" onClick={finalizeSignature} disabled={signBusy} className="gap-2">
                  {signBusy ? "Clôture..." : "Valider et clôturer"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent data-testid="devis-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer le devis par e-mail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Adresse e-mail du client" value={mailTo} onChange={(e) => setMailTo(e.target.value)} data-testid="devis-mail-to" />
            <Input placeholder="Objet" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="devis-mail-subject" />
            <Textarea className="h-32" placeholder="Message" value={mailMessage} onChange={(e) => setMailMessage(e.target.value)} data-testid="devis-mail-message" />
            <p className="text-xs text-muted-foreground">Le PDF du devis sera joint automatiquement. L'e-mail sera envoyé depuis l'adresse configurée pour votre boutique (Admin &gt; Boutiques &amp; Dépôts).</p>
          </div>
          <DialogFooter>
            <Button onClick={sendMail} disabled={sending} className="gap-2" data-testid="devis-mail-send-button">
              <Mail size={16} />{sending ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={articlePickerOpen} onOpenChange={setArticlePickerOpen}>
        <DialogContent data-testid="devis-article-picker-dialog" className="max-h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle>Ajouter un article du stock</DialogTitle></DialogHeader>
          <div className="relative shrink-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Rechercher un article, une catégorie, un ID..."
              value={articleSearch}
              onChange={(e) => setArticleSearch(e.target.value)}
              data-testid="devis-article-search"
            />
          </div>
          <div className="overflow-y-auto space-y-4 pr-1 -mr-1">
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">En stock ({articlesEnStock.length})</p>
              {articlesEnStock.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">Aucun article en stock ne correspond.</p>
              ) : (
                <div className="space-y-1" data-testid="devis-article-list-en-stock">
                  {articlesEnStock.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`devis-article-pick-${a.id}`}
                      onClick={() => addArticleFromStock(a)}
                      className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-2 hover:border-primary transition-colors duration-200 text-left"
                    >
                      <div className="w-9 h-9 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                        {a.photo_url ? <img src={`${UPLOADS_BASE}${a.photo_url}`} className="w-full h-full object-cover" alt="" /> : <Package size={15} className="text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{a.nom}</p>
                        <p className="text-xs text-muted-foreground">{a.categorie || "—"} · Qté: {a.quantite}</p>
                      </div>
                      <p className="text-sm font-semibold shrink-0">{Number(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Hors stock ({articlesHorsStock.length})</p>
              {articlesHorsStock.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">Aucun article hors stock ne correspond.</p>
              ) : (
                <div className="space-y-1" data-testid="devis-article-list-hors-stock">
                  {articlesHorsStock.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`devis-article-pick-${a.id}`}
                      onClick={() => addArticleFromStock(a)}
                      className="w-full flex items-center gap-3 rounded-lg border border-slate-400/40 bg-slate-500/5 p-2 hover:border-primary transition-colors duration-200 text-left opacity-80"
                    >
                      <div className="w-9 h-9 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                        {a.photo_url ? <img src={`${UPLOADS_BASE}${a.photo_url}`} className="w-full h-full object-cover" alt="" /> : <Package size={15} className="text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{a.nom}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{a.categorie || "—"} · Hors stock</p>
                      </div>
                      <p className="text-sm font-semibold shrink-0">{Number(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setArticlePickerOpen(false)} data-testid="devis-article-picker-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
